import type { DefLookup } from "../catalog";
import { PV_COMBINERS, PV_HOLDER_MAX_VDC } from "../catalog/pv-combiners";
import { resolveProtectionDevices } from "../protection-devices";
import type { ComponentDefinition, ComponentNode, Issue, System } from "../types";
import { buildGraph, splitV, v, type Graph, type Vertex } from "./graph";
import { circuitReach, matchesCircuitDefinition } from "./circuit-component-utils";

/** Follow each complete series string, never summing parallel panel voltages.
 * This uses declared STC Voc only; absence/cold correction stays unverified. */
function stringEvidence(
  graph: Graph, definitions: Map<string, ComponentDefinition>,
  start: Vertex, returnPost: Vertex, excludedNode: string,
): { panelIds: Set<string>; maxVoc: number | undefined; completeReturn: boolean } {
  const panelIds = new Set<string>();
  let best: number | undefined;
  let completeReturn = false;
  let visits = 0;
  const recurse = (seed: Vertex, seenPanels: Set<string>, voc: number | undefined) => {
    if (++visits > 1000) return;
    const reached = circuitReach(graph, seed, (_, { via }) =>
      via.kind !== "intra" || via.link.nodeId !== excludedNode);
    if (reached.has(returnPost) && seenPanels.size) {
      completeReturn = true;
      if (voc != null) best = Math.max(best ?? 0, voc);
    }
    for (const vertex of reached) {
      const { nodeId, terminalId } = splitV(vertex);
      const def = definitions.get(nodeId);
      if (def?.category !== "solar" || seenPanels.has(nodeId) ||
          def.terminals.find(t => t.id === terminalId)?.polarity !== "positive") continue;
      panelIds.add(nodeId);
      const nextSeen = new Set(seenPanels).add(nodeId);
      const nextVoc = voc != null && def.pvVoc != null && Number.isFinite(def.pvVoc) && def.pvVoc > 0 ? voc + def.pvVoc : undefined;
      for (const negative of def.terminals.filter(t => t.polarity === "negative")) recurse(v(nodeId, negative.id), nextSeen, nextVoc);
    }
  };
  recurse(start, new Set(), 0);
  // Do not claim a bound after cutting off an unusually complex series graph.
  return { panelIds, maxVoc: visits > 1000 ? undefined : best, completeReturn: visits <= 1000 && completeReturn };
}

/** Verifies drawn string-protection paths, not an installation or a chosen
 * fuse's coordination. Module Isc, cold Voc and exact products still matter. */
export function runPvCombinerDiagnostics(system: System, lookup: DefLookup): Issue[] {
  const issues: Issue[] = [];
  const graph = buildGraph(system, lookup, { excludeSense: true });
  const definitions = new Map<string, ComponentDefinition>();
  const nodes = new Map<string, ComponentNode>();
  for (const node of system.nodes) if (node.kind === "component") {
    const def = lookup(node.definitionId, node.customDefinition);
    if (def) { definitions.set(node.id, def); nodes.set(node.id, node); }
  }
  const holderBaseline = PV_COMBINERS.find(part => part.id === "dist-holder-gpv")!;
  const protection = resolveProtectionDevices(system, lookup);
  const goodHolders = new Set<string>();
  for (const device of protection) {
    const nodeId = device.nodeIds[0];
    const def = definitions.get(nodeId);
    const attached = system.cables.filter(c => c.fromNodeId === nodeId || c.toNodeId === nodeId);
    if (device.kind === "holder" && def && matchesCircuitDefinition(def, holderBaseline) &&
        device.family === "gPV" && device.ratingA != null && Number.isFinite(device.ratingA) &&
        device.ratingA > 0 && device.ratingA <= 30 && !device.conflicts.length &&
        !attached.some(c => c.fuseTypeOverride != null && c.fuseTypeOverride !== "gPV")) goodHolders.add(nodeId);
  }
  const goodMarkers = new Set(system.cables.filter(c =>
    c.fuseTypeOverride === "gPV" && c.fuseAmpOverride != null && Number.isFinite(c.fuseAmpOverride) &&
    c.fuseAmpOverride > 0 && c.fuseAmpOverride <= 30 &&
    // A contradictory holder declaration cannot be repaired by a cable label.
    ![c.fromNodeId, c.toNodeId].some(id =>
      definitions.get(id)?.id === "dist-holder-gpv" && !goodHolders.has(id)),
  ).map(c => c.id));

  for (const [nodeId, def] of definitions) {
    if (def.id !== "dist-pv-combiner-3") continue;
    const node = nodes.get(nodeId)!;
    const connected = system.cables.filter(c => c.fromNodeId === nodeId || c.toNodeId === nodeId);
    if (!connected.length) continue;
    const vars = { label: node.label ?? def.shortName ?? def.name };
    const issue = (code: string, severity: Issue["severity"], extra: Record<string, string | number> = {}, related: string[] = []) => issues.push({
      code, severity, title: { key: `diag.${code}.title`, vars: { ...vars, ...extra } },
      message: { key: `diag.${code}.message`, vars: { ...vars, ...extra } },
      fix: { key: `diag.${code}.fix`, vars: { ...vars, ...extra } },
      nodeIds: [nodeId, ...related], cableIds: connected.map(c => c.id),
    });
    if (!matchesCircuitDefinition(def, PV_COMBINERS[0])) {
      issue("pv-combiner-model-unverified", "info");
      continue;
    }
    // Generic copper diagram has no manufacturer voltage/current evidence.
    issue("pv-combiner-rating-unverified", "info");
    for (const index of [1, 2, 3]) {
      const positive = `pv${index}-pos`;
      const negative = `pv${index}-neg`;
      const attachedTo = (post: string) => connected.some(c =>
        (c.fromNodeId === nodeId && c.fromTerminalId === post) ||
        (c.toNodeId === nodeId && c.toTerminalId === post));
      if (!attachedTo(positive)) continue;
      const start = v(nodeId, positive);
      const evidence = stringEvidence(graph, definitions, start, v(nodeId, negative), nodeId);
      const full = circuitReach(graph, start, (_, { via }) => via.kind !== "intra" || via.link.nodeId !== nodeId);
      const solarPosts = [...full].filter(vertex => {
        const end = splitV(vertex);
        const part = definitions.get(end.nodeId);
        return part?.category === "solar" && part.terminals.find(t => t.id === end.terminalId)?.polarity === "positive";
      });
      if (!evidence.completeReturn) issue("pv-string-return-missing", "warning", { string: index });
      if (!solarPosts.length) continue;
      const unprotected = circuitReach(graph, start, (_, { via }) => via.kind === "cable"
        ? !goodMarkers.has(via.cableId)
        : via.link.nodeId !== nodeId && !goodHolders.has(via.link.nodeId));
      const reachableHolders = [...nodes.keys()].filter(id =>
        definitions.get(id)?.id === "dist-holder-gpv" && ["in", "out"].some(post => full.has(v(id, post))));
      const devices = protection.filter(device =>
        device.kind === "holder" ? reachableHolders.includes(device.nodeIds[0]) :
          device.kind === "cable" && device.family === "gPV" && device.terminals.some(post => full.has(post)));
      if (solarPosts.some(post => unprotected.has(post))) {
        const badHolder = reachableHolders.some(id => !goodHolders.has(id));
        issue(badHolder ? "pv-string-fuse-unrated" : devices.length ? "pv-string-protection-bypass" : "pv-string-protection-missing",
          "warning", { string: index }, reachableHolders);
      }
      if (evidence.maxVoc != null && evidence.maxVoc > PV_HOLDER_MAX_VDC) {
        for (const holder of reachableHolders.filter(id => {
          const part = definitions.get(id)!;
          return matchesCircuitDefinition(part, holderBaseline);
        })) issue("pv-holder-voltage-exceeded", "warning", {
          string: index, voltage: evidence.maxVoc, maxVoltage: PV_HOLDER_MAX_VDC,
        }, [holder]);
      }
    }
  }
  return issues;
}
